/**
 * Browser attribution capture (ported from /measure-marketer-pipeline/website/tracking.js
 * for direct React use). Same logic — captures click IDs + cookies + UTM +
 * first-touch values, persists to localStorage + first-party cookies (90d),
 * synthesizes _fbc when missing, generates UUIDv4 event_id on submit.
 *
 * Used here as a plain module (no `window.MMTracking` namespace) so the
 * React form can call captureTracking() / mintEventId() directly.
 */

const STORAGE_KEY = 'mm_tracking';
const RETENTION_DAYS = 90;

const TRACKED_URL_PARAMS = [
  'fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'li_fat_id', 'twclid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'mm_source',
];

const TRACKED_COOKIES = ['_fbp', '_fbc', '_ga', '_gid', '_gcl_aw', '_gcl_dc'];

// Reads the mm_tracking blob from BOTH localStorage AND the cross-subdomain
// cookie, then merges. The cookie carries data written by mm-tracking.js on
// a sibling subdomain (e.g. academy.measuremarketer.com) which the form's
// own localStorage on crm.measuremarketer.com can't see. Cookie wins for
// fields it has; local wins for fresh iframe-side fields (last_seen_at,
// parent_landing_page, etc.) that exist only on this origin.
function readStorage() {
  let local = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) local = JSON.parse(raw);
  } catch {}

  let cookie = {};
  try {
    const raw = getCookie(STORAGE_KEY);
    if (raw) cookie = JSON.parse(raw);
  } catch {}

  return { ...cookie, ...local };
}

function writeStorage(obj) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {}
}

function getCookie(name) {
  const prefix = name + '=';
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const p of parts) {
    if (p.indexOf(prefix) === 0) return decodeURIComponent(p.substring(prefix.length));
  }
  return '';
}

function setCookie(name, value, days) {
  let expires = '';
  if (days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + d.toUTCString();
  }
  const sec = window.location.protocol === 'https:' ? '; Secure' : '';
  const host = window.location.hostname;
  const labels = host.split('.');
  let domain = '';
  if (labels.length > 2 && !/^\d+(\.\d+){3}$/.test(host)) {
    domain = '; domain=.' + labels.slice(-2).join('.');
  }
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/${domain}; SameSite=Lax${sec}`;
}

// A stable per-visitor id used to upsert partial-capture rows as the user types
// (and, because it lives in the same storage blob, it rides along into
// cookies_data at real submission so the capture can be retired). Minted once,
// then reused across the visitor's session.
export function getCaptureId() {
  const stored = readStorage();
  if (stored.capture_id) return stored.capture_id;
  const id = 'cap_' + uuidv4();
  stored.capture_id = id;
  writeStorage(stored);
  try { setCookie(STORAGE_KEY, JSON.stringify(stored), RETENTION_DAYS); } catch {}
  return id;
}

export function uuidv4() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function subdomainIndex() {
  const host = window.location.hostname;
  if (/^\d+(\.\d+){3}$/.test(host)) return 1;
  if (host === 'localhost') return 0;
  return (host.match(/\./g) || []).length;
}

function buildFbc(fbclid) {
  return `fb.${subdomainIndex()}.${Date.now()}.${fbclid}`;
}

function extractGaClientId(gaCookie) {
  if (!gaCookie) return '';
  const parts = gaCookie.split('.');
  if (parts.length < 4) return '';
  return parts.slice(-2).join('.');
}

/**
 * Capture all available attribution signals and persist them.
 * Returns the merged data blob.
 */
export function captureTracking() {
  const stored = readStorage();
  const params = new URL(window.location.href).searchParams;
  const now = new Date().toISOString();
  const data = { ...stored };

  data.last_seen_at = now;
  data.landing_page_url = window.location.href;
  data.referrer = document.referrer || '';
  data.client_user_agent = navigator.userAgent || '';

  TRACKED_URL_PARAMS.forEach((p) => {
    const v = params.get(p);
    if (v) data[p] = v;
  });

  TRACKED_COOKIES.forEach((c) => {
    const v = getCookie(c);
    if (v) data[c] = v;
  });

  if (data._ga) data.ga_client_id = extractGaClientId(data._ga);

  // GA4 per-property session cookies are named `_ga_<MEASUREMENT_ID>`
  // (e.g. `_ga_F3FSFWGKSS`). The suffix is dynamic so we can't add them
  // to TRACKED_COOKIES - walk all cookies and capture every match.
  // Value format: `GS1.1.<sessionStart>.<sessionCount>...` - we parse
  // the start timestamp out as `ga_session_id` so server-side GA4
  // Measurement Protocol sends can stitch back to the right session.
  try {
    const allCookies = document.cookie ? document.cookie.split('; ') : [];
    for (const part of allCookies) {
      const eq = part.indexOf('=');
      if (eq <= 0) continue;
      const name = part.substring(0, eq);
      if (!name.startsWith('_ga_')) continue;
      const value = decodeURIComponent(part.substring(eq + 1));
      data[name] = value;
      if (!data.ga_session_id) {
        // Two formats: old `GS1.1.<sessionId>.<count>...` (dot-delimited) and
        // new `GS2.1.s<sessionId>$o<count>$...` ($-delimited with letter tags).
        // The optional `s?` captures the session id in both.
        const m = value.match(/^GS\d+\.\d+\.s?(\d+)/);
        if (m) data.ga_session_id = m[1];
      }
    }
  } catch {}

  if (!data._fbc && data.fbclid) {
    data._fbc = buildFbc(data.fbclid);
    setCookie('_fbc', data._fbc, RETENTION_DAYS);
  }

  if (!data.first_touch_at) {
    data.first_touch_at = now;
    data.first_landing_page = data.landing_page_url;
    data.first_referrer = data.referrer;
    if (data.fbclid) data.first_fbclid = data.fbclid;
    if (data.gclid) data.first_gclid = data.gclid;
    if (data.utm_source) data.first_utm_source = data.utm_source;
    if (data.utm_campaign) data.first_utm_campaign = data.utm_campaign;
  }

  writeStorage(data);
  setCookie(STORAGE_KEY, JSON.stringify(data), RETENTION_DAYS);
  return data;
}

/**
 * Mint a fresh event_id + timestamp for a form submission.
 * Returns an object the caller merges into the cookies_data payload.
 */
export function mintEventMeta() {
  return {
    event_id: uuidv4(),
    original_event_time: new Date().toISOString(),
  };
}

/**
 * Merge parent-page URL context into stored tracking data. When the form
 * is embedded in an iframe on academy.measuremarketer.com, window.location
 * inside the iframe points at crm.measuremarketer.com (no UTMs). The
 * parent page forwards its own search string + referrer via postMessage;
 * we parse the UTMs out and persist alongside everything else captured
 * client-side.
 */
function mergeParentContext({ search, href, referrer, tracking }) {
  const stored = readStorage();
  const data = { ...stored };
  let dirty = false;

  // If parent forwarded the full mm_tracking blob (because mm-tracking.js is
  // installed in their <head>), merge wholesale. This carries cookies the
  // iframe physically can't read (parent's _ga / _fbp / _ga_<id>) plus
  // pinned first-touch values from earlier visits.
  if (tracking && typeof tracking === 'object') {
    Object.keys(tracking).forEach((k) => {
      // Don't let stale parent state overwrite iframe-side fresh fields
      if (k === 'last_seen_at' || k === 'event_id' || k === 'original_event_time') return;
      if (tracking[k] != null && tracking[k] !== '' && data[k] !== tracking[k]) {
        data[k] = tracking[k];
        dirty = true;
      }
    });
  }

  // Fallback: parse UTMs out of parent's search string (when mm-tracking.js
  // isn't installed but the embed snippet still forwards location.search).
  if (search) {
    try {
      const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
      TRACKED_URL_PARAMS.forEach((p) => {
        const v = params.get(p);
        if (v && data[p] !== v) {
          data[p] = v;
          dirty = true;
        }
      });
    } catch {}
  }

  if (href && data.parent_landing_page !== href) {
    data.parent_landing_page = href;
    // The user's real landing page is the parent's URL, not the iframe's.
    // Overwrite landing_page_url so attribution dashboards aren't confused.
    data.landing_page_url = href;
    if (!data.first_landing_page || data.first_landing_page.includes('/form/')) {
      data.first_landing_page = href;
    }
    dirty = true;
  }
  if (referrer && data.parent_referrer !== referrer) {
    data.parent_referrer = referrer;
    if (!data.first_referrer || data.first_referrer.includes(window.location.host)) {
      data.first_referrer = referrer;
    }
    dirty = true;
  }

  // Re-set first_utm_* if this is the first time we're seeing UTMs at all
  // (i.e. before postMessage we didn't have them).
  if (dirty) {
    if (data.utm_source && !data.first_utm_source) data.first_utm_source = data.utm_source;
    if (data.utm_campaign && !data.first_utm_campaign) data.first_utm_campaign = data.utm_campaign;
    writeStorage(data);
    setCookie(STORAGE_KEY, JSON.stringify(data), RETENTION_DAYS);
  }
  return data;
}

/**
 * Wire the parent-context postMessage receiver. Call once on mount. Only
 * listens to messages whose `source` field equals the iframe's parent
 * window, so it can't be poisoned by a third-party tab that happens to
 * also postMessage to this origin.
 */
export function installParentContextListener() {
  if (typeof window === 'undefined') return () => {};
  if (window.parent === window) return () => {};

  const handler = (e) => {
    if (e.source !== window.parent) return;
    if (!e.data || e.data.source !== 'mm-crm-parent') return;
    if (e.data.type !== 'mm:parent-context') return;
    try {
      mergeParentContext(e.data.payload || {});
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('mm parent-context merge failed:', err?.message);
    }
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
